import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import {
  CheckCircle, XCircle, Calendar, FileText, Clock, DollarSign,
  ExternalLink, MessageSquare, Paperclip, Download, File, FileSpreadsheet
} from "lucide-react";

// Helper function to determine if a color is light or dark
const isLightColor = (hexColor) => {
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5;
};

// File type icons
const getFileIcon = (fileType) => {
  if (fileType === '.pdf') return <FileText className="w-5 h-5 text-red-500" />;
  if (fileType === '.doc' || fileType === '.docx') return <File className="w-5 h-5 text-blue-500" />;
  if (fileType === '.xls' || fileType === '.xlsx') return <FileSpreadsheet className="w-5 h-5 text-green-500" />;
  return <FileText className="w-5 h-5 text-muted-foreground" />;
};

// Format file size
const formatFileSize = (bytes) => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

const ProspectView = () => {
  const { id } = useParams();
  const [proposal, setProposal] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [showDeclineDialog, setShowDeclineDialog] = useState(false);
  const [showMeetingDialog, setShowMeetingDialog] = useState(false);
  const [notes, setNotes] = useState("");
  const [actionComplete, setActionComplete] = useState(null);

  useEffect(() => {
    loadProposal();
    loadAttachments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadProposal = async () => {
    try {
      const response = await axios.get(`${API}/public/proposals/${id}`);
      setProposal(response.data);
    } catch (error) {
      toast.error("Proposal not found or expired");
    } finally {
      setLoading(false);
    }
  };

  const loadAttachments = async () => {
    try {
      const response = await axios.get(`${API}/public/proposals/${id}/attachments`);
      setAttachments(response.data);
    } catch (error) {
      console.error("Failed to load attachments");
    }
  };

  const downloadAttachment = (attachmentId, filename) => {
    // Open in new tab to trigger download
    window.open(`${API}/public/proposals/${id}/attachments/${attachmentId}`, '_blank');
  };

  const handleAction = async (action) => {
    setActionLoading(true);
    try {
      const response = await axios.post(`${API}/public/proposals/${id}/action`, {
        action,
        notes: action !== "accept" ? notes : null
      });

      if (action === "accept") {
        setActionComplete("accepted");
        toast.success("Proposal accepted! The advisor has been notified.");
      } else if (action === "decline") {
        setActionComplete("declined");
        setShowDeclineDialog(false);
        toast.success("The advisor has been notified of your decision.");
      } else if (action === "schedule_meeting") {
        setShowMeetingDialog(false);
        if (response.data.discovery_link) {
          window.open(response.data.discovery_link, "_blank");
        }
        toast.success("Meeting request sent to advisor!");
      }
    } catch (error) {
      toast.error("Failed to process your request");
    } finally {
      setActionLoading(false);
      setNotes("");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center">
        <div className="animate-pulse text-forest font-display text-2xl">Loading proposal...</div>
      </div>
    );
  }

  if (!proposal) {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center">
        <div className="text-center">
          <FileText className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h1 className="font-display text-2xl text-forest mb-2">Proposal Not Found</h1>
          <p className="text-muted-foreground">This proposal may have expired or been removed.</p>
        </div>
      </div>
    );
  }

  const brandColors = proposal.brand_colors || {
    primary: "#0f392b",
    secondary: "#f5f2eb",
    accent: "#c1666b"
  };

  if (actionComplete) {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center p-6" data-testid="action-complete">
        <div className="text-center max-w-md">
          {actionComplete === "accepted" ? (
            <>
              <div 
                className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
                style={{ backgroundColor: brandColors.accent + "20" }}
              >
                <CheckCircle className="w-10 h-10" style={{ color: brandColors.accent }} />
              </div>
              <h1 className="font-display text-3xl font-bold mb-4" style={{ color: brandColors.primary }}>
                Proposal Accepted!
              </h1>
              <p className="text-muted-foreground text-lg">
                Thank you for accepting this proposal. {proposal.advisor_name} has been notified and will reach out with next steps.
              </p>
            </>
          ) : (
            <>
              <div className="w-20 h-20 bg-stone/30 rounded-full flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-10 h-10 text-muted-foreground" />
              </div>
              <h1 className="font-display text-3xl font-bold text-forest mb-4">Thank You</h1>
              <p className="text-muted-foreground text-lg">
                We've notified {proposal.advisor_name} of your decision. Feel free to reach out if you change your mind.
              </p>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: brandColors.secondary }} data-testid="prospect-view">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-stone">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {proposal.logo_url ? (
              <img src={proposal.logo_url} alt={proposal.company_name} className="h-10 object-contain" />
            ) : (
              <div 
                className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: brandColors.primary }}
              >
                <FileText className="w-5 h-5 text-white" />
              </div>
            )}
            <span className="font-display font-semibold" style={{ color: brandColors.primary }}>
              {proposal.company_name || proposal.advisor_name}
            </span>
          </div>
          {(proposal.status === "sent" || proposal.status === "viewed") && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (proposal.discovery_link) {
                    // Go directly to calendar, notify advisor in background
                    window.open(proposal.discovery_link, "_blank");
                    handleAction("schedule_meeting");
                  } else {
                    setShowMeetingDialog(true);
                  }
                }}
                data-testid="schedule-meeting-btn"
              >
                <Calendar className="w-4 h-4 mr-2" />
                Schedule Meeting
              </Button>
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-3xl mx-auto px-6 py-12">
        {/* Proposal Header */}
        <div className="mb-12 animate-fadeIn">
          <p className="font-label text-sm uppercase tracking-widest mb-4" style={{ color: brandColors.accent }}>
            Proposal for {proposal.prospect_name}
          </p>
          <h1 className="font-display text-4xl md:text-5xl font-bold mb-6" style={{ color: brandColors.primary }}>
            {proposal.title}
          </h1>
          {proposal.description && (
            <p className="text-lg leading-relaxed" style={{ color: brandColors.primary + "99" }}>
              {proposal.description}
            </p>
          )}
          
          <div className="flex flex-wrap gap-6 mt-8">
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5" style={{ color: brandColors.primary + "60" }} />
              <span className="font-mono text-sm" style={{ color: brandColors.primary + "80" }}>
                Created {new Date(proposal.created_at).toLocaleDateString()}
              </span>
            </div>
            {proposal.valid_until && (
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5" style={{ color: brandColors.primary + "60" }} />
                <span className="font-mono text-sm" style={{ color: brandColors.primary + "80" }}>
                  Valid until {new Date(proposal.valid_until).toLocaleDateString()}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Sections */}
        <div className="space-y-8">
          {proposal.sections?.map((section, index) => (
            <Card 
              key={section.id || index} 
              className="border-stone bg-white animate-fadeIn"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <CardContent className="p-8">
                {section.type === "text" && (
                  <div>
                    {section.content.title && (
                      <h2 className="font-display text-2xl font-semibold mb-4" style={{ color: brandColors.primary }}>
                        {section.content.title}
                      </h2>
                    )}
                    <p className="leading-relaxed whitespace-pre-wrap" style={{ color: brandColors.primary + "cc" }}>
                      {section.content.body}
                    </p>
                  </div>
                )}

                {section.type === "pricing" && (
                  <div>
                    <h2 className="font-display text-2xl font-semibold mb-6" style={{ color: brandColors.primary }}>
                      Investment
                    </h2>
                    
                    {/* ONE-TIME PAYMENT - Line items */}
                    {section.content?.payment_type === "one_time" && (
                      <div className="space-y-4">
                        {(section.content?.items || []).filter(item => item.description || item.amount > 0).map((item, itemIndex) => (
                          <div key={itemIndex} className="flex justify-between items-center py-3 border-b border-stone last:border-0">
                            <span style={{ color: brandColors.primary }}>{item.description || 'Service'}</span>
                            <span className="font-mono font-semibold" style={{ color: brandColors.primary }}>
                              ${parseFloat(item.amount || 0).toLocaleString()}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* SPLIT PAYMENTS - Installments */}
                    {section.content?.payment_type === "split" && (
                      <div className="space-y-4">
                        {(section.content?.split_payments || []).map((payment, paymentIndex) => (
                          <div key={paymentIndex} className="flex justify-between items-center py-3 border-b border-stone last:border-0">
                            <div className="flex items-center gap-3">
                              <div 
                                className="w-7 h-7 rounded-full flex items-center justify-center text-sm font-medium text-white"
                                style={{ backgroundColor: brandColors.primary }}
                              >
                                {paymentIndex + 1}
                              </div>
                              <span style={{ color: brandColors.primary }}>{payment.due_description || `Payment ${paymentIndex + 1}`}</span>
                            </div>
                            <span className="font-mono font-semibold" style={{ color: brandColors.primary }}>
                              ${parseFloat(payment.amount || 0).toLocaleString()}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* SUBSCRIPTION - Recurring */}
                    {section.content?.payment_type === "subscription" && (
                      <div className="space-y-4">
                        <div className="flex justify-between items-center py-3 border-b border-stone">
                          <div>
                            <span style={{ color: brandColors.primary }}>
                              {section.content?.subscription?.description || 'Subscription'}
                            </span>
                            <p className="text-sm text-muted-foreground mt-1">
                              Billed {section.content?.subscription?.frequency || 'monthly'}
                            </p>
                          </div>
                          <span className="font-mono font-semibold" style={{ color: brandColors.primary }}>
                            ${parseFloat(section.content?.subscription?.amount || 0).toLocaleString()}
                            <span className="text-sm font-normal text-muted-foreground">
                              /{section.content?.subscription?.frequency === 'annually' ? 'year' : 
                                section.content?.subscription?.frequency === 'quarterly' ? 'quarter' : 'month'}
                            </span>
                          </span>
                        </div>
                      </div>
                    )}

                    {/* HYBRID - Setup fee + Subscription */}
                    {section.content?.payment_type === "hybrid" && (
                      <div className="space-y-4">
                        {/* Onboarding/Setup Fee */}
                        {section.content?.onboarding_fee > 0 && (
                          <div className="flex justify-between items-center py-3 border-b border-stone">
                            <span style={{ color: brandColors.primary }}>
                              {section.content?.onboarding_description || 'Setup / Onboarding Fee'}
                            </span>
                            <span className="font-mono font-semibold" style={{ color: brandColors.primary }}>
                              ${parseFloat(section.content?.onboarding_fee || 0).toLocaleString()}
                            </span>
                          </div>
                        )}
                        
                        {/* Recurring Subscription */}
                        {section.content?.subscription?.amount > 0 && (
                          <div className="flex justify-between items-center py-3 border-b border-stone last:border-0">
                            <div>
                              <span style={{ color: brandColors.primary }}>
                                {section.content?.subscription?.description || 'Recurring Subscription'}
                              </span>
                              <p className="text-sm text-muted-foreground mt-1">
                                Billed {section.content?.subscription?.frequency || 'monthly'}
                                {section.content?.subscription_start && section.content?.subscription_start !== 'immediate' && (
                                  <span> • Starts {section.content?.subscription_start.replace('after_', '')} days after setup</span>
                                )}
                              </p>
                            </div>
                            <span className="font-mono font-semibold" style={{ color: brandColors.primary }}>
                              ${parseFloat(section.content?.subscription?.amount || 0).toLocaleString()}
                              <span className="text-sm font-normal text-muted-foreground">
                                /{section.content?.subscription?.frequency === 'annually' ? 'year' : 
                                  section.content?.subscription?.frequency === 'quarterly' ? 'quarter' : 'month'}
                              </span>
                            </span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* LEGACY FALLBACK - For old proposals without payment_type */}
                    {!section.content?.payment_type && section.content?.items && (
                      <div className="space-y-4">
                        {(section.content?.items || []).filter(item => item.description || item.amount > 0).map((item, itemIndex) => (
                          <div key={itemIndex} className="flex justify-between items-center py-3 border-b border-stone last:border-0">
                            <span style={{ color: brandColors.primary }}>{item.description || 'Service'}</span>
                            <span className="font-mono font-semibold" style={{ color: brandColors.primary }}>
                              ${parseFloat(item.amount || 0).toLocaleString()}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {section.type === "aum_pricing" && (
                  <div>
                    <h2 className="font-display text-2xl font-semibold mb-6" style={{ color: brandColors.primary }}>
                      Advisory Fee
                    </h2>
                    
                    {/* AUM Amount */}
                    {section.content.aum > 0 && (
                      <div className="mb-6 p-4 rounded-lg" style={{ backgroundColor: `${brandColors.primary}08` }}>
                        <p className="text-sm text-muted-foreground mb-1">Assets Under Management</p>
                        <p className="font-mono text-2xl font-bold" style={{ color: brandColors.primary }}>
                          ${section.content.aum?.toLocaleString()}
                        </p>
                      </div>
                    )}
                    
                    {/* Calculated Fee */}
                    {section.content.calculated_fee > 0 && (
                      <div className="flex justify-between items-center py-4 border-b border-stone">
                        <span style={{ color: brandColors.primary }}>Annual Advisory Fee</span>
                        <span className="font-mono text-xl font-bold" style={{ color: brandColors.primary }}>
                          ${section.content.calculated_fee?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                    )}
                    
                    {/* Fee Display Text */}
                    {section.content.fee_display && (
                      <p className="text-sm text-muted-foreground mt-2">
                        {section.content.fee_display}
                      </p>
                    )}
                    
                    {/* Breakdown - only shown if display_preference is full_breakdown */}
                    {section.content.display_preference === "full_breakdown" && section.content.breakdown && section.content.breakdown.length > 1 && (
                      <div className="mt-4 pt-4 border-t border-stone">
                        <p className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Fee Breakdown</p>
                        {section.content.breakdown.map((tier, i) => (
                          <div key={i} className="flex justify-between text-sm py-1">
                            <span className="text-muted-foreground">{tier.range} @ {tier.rate}%</span>
                            <span className="font-mono">${tier.amount?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Additional Services */}
                    {section.content.additional_services && section.content.additional_services.length > 0 && (
                      <div className="mt-6 pt-4 border-t border-stone">
                        <p className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Additional Services</p>
                        {section.content.additional_services.map((service, i) => (
                          <div key={i} className="flex justify-between items-center py-2">
                            <span style={{ color: brandColors.primary }}>{service.description}</span>
                            <span className="font-mono" style={{ color: brandColors.primary }}>
                              {service.included ? "Included" : `$${service.amount?.toLocaleString()}`}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {section.type === "image" && section.content.url && (
                  <div>
                    <img 
                      src={section.content.url} 
                      alt={section.content.caption || ""}
                      className="w-full rounded-lg"
                    />
                    {section.content.caption && (
                      <p className="text-sm text-muted-foreground mt-3 text-center">
                        {section.content.caption}
                      </p>
                    )}
                  </div>
                )}

                {section.type === "signature" && (
                  <div>
                    <p className="font-label text-sm uppercase tracking-wider text-muted-foreground mb-4">
                      {section.content.label || "Signature"}
                    </p>
                    <div className="h-24 border-b-2 border-dashed border-stone"></div>
                  </div>
                )}

                {section.type === "advisor_info" && (
                  <div>
                    <h2 className="font-display text-2xl font-semibold mb-6" style={{ color: brandColors.primary }}>
                      About Your Advisor
                    </h2>
                    <div className="flex items-start gap-4">
                      {section.content.profile_picture_url ? (
                        <img 
                          src={section.content.profile_picture_url} 
                          alt={section.content.name || "Advisor"}
                          className="w-20 h-20 rounded-full object-cover border-2 shadow-sm"
                          style={{ borderColor: brandColors.primary + '40' }}
                          data-testid="advisor-profile-picture"
                        />
                      ) : (
                        <div 
                          className="w-20 h-20 rounded-full flex items-center justify-center text-2xl font-semibold"
                          style={{ backgroundColor: brandColors.primary + '15', color: brandColors.primary }}
                        >
                          {(section.content.name || "A").charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div className="space-y-2 flex-1">
                        <p className="text-lg font-semibold" style={{ color: brandColors.primary }}>
                          {section.content.name} {section.content.designations && <span className="font-normal text-base">{section.content.designations}</span>}
                        </p>
                        {section.content.title && (
                          <p style={{ color: brandColors.primary + "cc" }}>{section.content.title}</p>
                        )}
                        {section.content.company_name && (
                          <p style={{ color: brandColors.primary + "cc" }}>{section.content.company_name}</p>
                        )}
                        <div className="flex flex-wrap gap-4 mt-4 text-sm" style={{ color: brandColors.primary + "aa" }}>
                          {section.content.phone && <span>{section.content.phone}</span>}
                          {section.content.email && <span>{section.content.email}</span>}
                        </div>
                        {section.content.company_website && (
                          <a 
                            href={section.content.company_website.startsWith('http') ? section.content.company_website : `https://${section.content.company_website}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm underline mt-2 inline-block"
                            style={{ color: brandColors.accent }}
                          >
                            {section.content.company_website}
                          </a>
                        )}
                        {section.content.broker_check_link && (
                          <a 
                            href={section.content.broker_check_link.startsWith('http') ? section.content.broker_check_link : `https://${section.content.broker_check_link}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 mt-3 text-xs px-3 py-1.5 rounded-full transition-colors"
                            style={{ 
                              backgroundColor: brandColors.primary + "15", 
                              color: brandColors.primary 
                            }}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                            </svg>
                            FINRA BrokerCheck Verified
                            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                              <polyline points="15 3 21 3 21 9"/>
                              <line x1="10" y1="14" x2="21" y2="3"/>
                            </svg>
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {section.type === "brokercheck" && (
                  <div>
                    <h2 className="font-display text-2xl font-semibold mb-4" style={{ color: brandColors.primary }}>
                      Verify My Credentials
                    </h2>
                    <p className="mb-4" style={{ color: brandColors.primary + "cc" }}>
                      {section.content.description || "You can verify my professional background and credentials through FINRA BrokerCheck."}
                    </p>
                    {section.content.link && (
                      <a 
                        href={section.content.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white"
                        style={{ backgroundColor: brandColors.primary }}
                      >
                        View on BrokerCheck
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                )}

                {section.type === "onboarding_software" && (
                  <div>
                    <h2 className="font-display text-2xl font-semibold mb-4" style={{ color: brandColors.primary }}>
                      {section.content.title || "Get Started"}
                    </h2>
                    <p className="mb-4" style={{ color: brandColors.primary + "cc" }}>
                      {section.content.description || "Complete your onboarding to get started with our planning tools."}
                    </p>
                    {section.content.link && (
                      <a 
                        href={section.content.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white"
                        style={{ backgroundColor: brandColors.accent }}
                      >
                        {section.content.button_text || "Start Onboarding"}
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Total Amount */}
        {proposal.total_amount > 0 && (
          <Card className="border-2 mt-8 animate-fadeIn" style={{ borderColor: brandColors.primary }}>
            <CardContent className="p-8">
              {(() => {
                // Calculate breakdown from sections
                let pricingTotal = 0;
                let aumFeeTotal = 0;
                
                proposal.sections?.forEach(s => {
                  if (s.type === "pricing" && s.content?.items) {
                    s.content.items.forEach(item => {
                      pricingTotal += parseFloat(item.amount) || 0;
                    });
                  }
                  if (s.type === "aum_pricing" && s.content?.calculated_fee) {
                    aumFeeTotal += parseFloat(s.content.calculated_fee) || 0;
                  }
                });
                
                const hasBothTypes = pricingTotal > 0 && aumFeeTotal > 0;
                
                return (
                  <div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <DollarSign className="w-8 h-8" style={{ color: brandColors.primary }} />
                        <span className="font-display text-xl font-semibold" style={{ color: brandColors.primary }}>
                          Total Investment
                        </span>
                      </div>
                      <span className="font-mono text-3xl font-bold" style={{ color: brandColors.primary }}>
                        ${proposal.total_amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                    
                    {hasBothTypes && (
                      <div className="mt-4 pt-4 border-t border-stone/30 space-y-2">
                        <div className="flex justify-between text-sm">
                          <span style={{ color: brandColors.primary + "99" }}>Services & fees</span>
                          <span className="font-mono" style={{ color: brandColors.primary + "99" }}>
                            ${pricingTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span style={{ color: brandColors.primary + "99" }}>Annual advisory fee</span>
                          <span className="font-mono" style={{ color: brandColors.primary + "99" }}>
                            ${aumFeeTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        )}

        {/* Attachments */}
        {attachments.length > 0 && (
          <Card className="border-stone mt-8 animate-fadeIn">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6">
                <Paperclip className="w-6 h-6" style={{ color: brandColors.primary }} />
                <h2 className="font-display text-xl font-semibold" style={{ color: brandColors.primary }}>
                  Attached Documents
                </h2>
              </div>
              <div className="grid gap-3">
                {attachments.map((attachment) => (
                  <div
                    key={attachment.attachment_id}
                    className="flex items-center gap-4 p-4 rounded-lg border border-stone hover:border-forest/30 transition-colors cursor-pointer"
                    onClick={() => downloadAttachment(attachment.attachment_id, attachment.filename)}
                  >
                    {getFileIcon(attachment.file_type)}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate" style={{ color: brandColors.primary }}>
                        {attachment.filename}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {formatFileSize(attachment.size)}
                      </p>
                    </div>
                    <Button variant="outline" size="sm">
                      <Download className="w-4 h-4 mr-2" />
                      Download
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Status Banner */}
        {proposal.status !== "sent" && proposal.status !== "viewed" && (
          <div className="mt-8 p-6 rounded-xl text-center" style={{ backgroundColor: brandColors.primary + "10" }}>
            {proposal.status === "accepted" && (
              <div className="flex items-center justify-center gap-3">
                <CheckCircle className="w-6 h-6" style={{ color: brandColors.accent }} />
                <span className="font-semibold" style={{ color: brandColors.primary }}>
                  This proposal has been accepted
                </span>
              </div>
            )}
            {proposal.status === "declined" && (
              <div className="flex items-center justify-center gap-3">
                <XCircle className="w-6 h-6 text-muted-foreground" />
                <span className="font-semibold text-muted-foreground">
                  This proposal has been declined
                </span>
              </div>
            )}
            {proposal.status === "draft" && (
              <span className="text-muted-foreground">
                This proposal is still being prepared
              </span>
            )}
          </div>
        )}
      </main>

      {/* Sticky Action Bar */}
      {(proposal.status === "sent" || proposal.status === "viewed") && (
        <div className="fixed bottom-0 left-0 right-0 glass border-t border-stone shadow-lg">
          <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
            <Button
              variant="outline"
              size="lg"
              className="flex-1 sm:flex-none border-2"
              onClick={() => setShowDeclineDialog(true)}
              data-testid="decline-proposal-btn"
            >
              <XCircle className="w-5 h-5 mr-2" />
              Decline
            </Button>
            <Button
              size="lg"
              className="flex-1 sm:flex-none px-8"
              style={{ 
                backgroundColor: brandColors.accent,
                color: isLightColor(brandColors.accent) ? '#000000' : '#ffffff'
              }}
              onClick={() => handleAction("accept")}
              disabled={actionLoading}
              data-testid="accept-proposal-btn"
            >
              <CheckCircle className="w-5 h-5 mr-2" />
              {actionLoading ? "Processing..." : "Accept Proposal"}
            </Button>
          </div>
        </div>
      )}

      {/* Decline Dialog */}
      <Dialog open={showDeclineDialog} onOpenChange={setShowDeclineDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display">Decline Proposal</DialogTitle>
            <DialogDescription>
              Let {proposal.advisor_name} know why you're declining. This feedback is optional but helpful.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Share your feedback (optional)..."
            className="min-h-[100px]"
          />
          <div className="flex justify-end gap-3 mt-4">
            <Button variant="outline" onClick={() => setShowDeclineDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => handleAction("decline")}
              disabled={actionLoading}
            >
              {actionLoading ? "Declining..." : "Decline Proposal"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Meeting Dialog */}
      <Dialog open={showMeetingDialog} onOpenChange={setShowMeetingDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display">Schedule a Meeting</DialogTitle>
            <DialogDescription>
              Have questions? Schedule a meeting with {proposal.advisor_name} to discuss the proposal.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="What would you like to discuss? (optional)..."
            className="min-h-[100px]"
          />
          {proposal.discovery_link ? (
            <Button
              className="w-full"
              style={{ backgroundColor: brandColors.primary }}
              onClick={() => {
                // Open calendar directly, then notify advisor in background
                window.open(proposal.discovery_link, "_blank");
                handleAction("schedule_meeting");
              }}
              disabled={actionLoading}
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Open Scheduling Page
            </Button>
          ) : (
            <Button
              className="w-full"
              style={{ backgroundColor: brandColors.primary }}
              onClick={() => handleAction("schedule_meeting")}
              disabled={actionLoading}
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              {actionLoading ? "Sending..." : "Request Meeting"}
            </Button>
          )}
        </DialogContent>
      </Dialog>

      {/* Bottom padding for sticky bar */}
      {proposal.status === "sent" && <div className="h-24"></div>}
    </div>
  );
};

export default ProspectView;
